import React, { useEffect, useState } from 'react';
import { Box, TextField, Autocomplete, CircularProgress } from '@mui/material';
import { fetchContestGroupings } from '../api/contests';
import TimeInput from "../utils/TimeInput";

// Define the FormField type
export type FormField = {
    field_name: string;
    display_label: string;
    data_type: string;
    required: boolean;
    main_result_field?:boolean;
};

// Props for the DynamicFormFields component
export type DynamicFormFieldsProps = {
    formFields: FormField[];
    formData: { [key: string]: any };
    onInputChange: (fieldName: string, value: any) => void;
    contestId?: string; // Optional contestId to fetch groupings if available

};

const PARTICIPANT_DATA_LIMIT = 490; // Character limit for participant_data

const DynamicFormFields: React.FC<DynamicFormFieldsProps> = ({ formFields, formData, onInputChange, contestId }) => {
    const [groupings, setGroupings] = useState<string[]>([]);
    const [loadingGroupings, setLoadingGroupings] = useState(false);

    useEffect(() => {
        if (contestId) {
            setLoadingGroupings(true);
            fetchContestGroupings(contestId)
                .then((data) => setGroupings(data))
                .catch((error) => console.error('Error fetching groupings:', error))
                .finally(() => setLoadingGroupings(false));
        }
    }, [contestId]);

    const renderField = (field: FormField) => {
        const value = formData[field.field_name] || '';

        if (field.field_name === 'grouping') {
            return (
                <Autocomplete
                    freeSolo
                    options={groupings}
                    value={value}
                    onChange={(_, newValue) => onInputChange(field.field_name, newValue)}
                    onInputChange={(_, newValue) => onInputChange(field.field_name, newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={field.display_label}
                            variant="outlined"
                            required={field.required}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loadingGroupings ? <CircularProgress size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    fullWidth
                    style={{ marginBottom: '16px' }}
                />
            );
        }

        if (field.field_name === 'participant_data') {
            return (
                <TextField
                    key={field.field_name}
                    label={field.display_label}
                    type="text"
                    variant="outlined"
                    fullWidth
                    required={field.required}
                    value={value}
                    multiline
                    rows={4}
                    inputProps={{ maxLength: PARTICIPANT_DATA_LIMIT }}
                    onChange={(e) => onInputChange(field.field_name, e.target.value)}
                    helperText={`${value.length}/${PARTICIPANT_DATA_LIMIT} characters`}
                    style={{ marginBottom: '16px' }}
                />
            );
        }

        switch (field.data_type) {
            case 'number':
                return (
                    <TextField
                        key={field.field_name}
                        label={field.display_label}
                        type="number"
                        variant="outlined"
                        fullWidth
                        required={field.required}
                        value={value}
                        onChange={(e) => onInputChange(field.field_name, e.target.value)}
                        style={{ marginBottom: '16px' }}
                    />
                );
            case 'text':
                return (
                    <TextField
                        key={field.field_name}
                        label={field.display_label}
                        type="text"
                        variant="outlined"
                        fullWidth
                        required={field.required}
                        value={value}
                        onChange={(e) => onInputChange(field.field_name, e.target.value)}
                        style={{ marginBottom: '16px' }}
                    />
                );
            case 'time':
                return (
                    <TimeInput
                        key={field.field_name}
                        label={field.display_label}
                        value={value}
                        onChange={(newValue) => onInputChange(field.field_name, newValue)}
                        required={field.required}
                        helperText="Введите время в формате чч:мм:сс"
                    />
                );

            default:
                return null;
        }
    };

    return (
        <Box>
            {formFields.map((field) => (
                <Box key={field.field_name} mb={2}>
                    {renderField(field)}
                </Box>
            ))}
        </Box>
    );
};

export default DynamicFormFields;
